import React, { useState, useMemo, useRef } from 'react';
import emojiRegex from 'emoji-regex';
import CardService from '../../molecules/CardService';
import BodyLargeSemibold from '../../atoms/Text/BodyLargeSemibold';
import SortIcon from '../../atoms/Icons/SortIcon';
import Tabs from '../../atoms/Tabs';
import FilterIcon from '../../atoms/Icons/FilterIcon';
import SearchServices from '../FilterMenu/Search';
import convertStringToSlug from 'ui/helpers/utils/convertStringToSlug';
import { IServicesPackages } from './interface';
import { useRouter } from '../../../hooks/useCustomRouter';
import ProductNotFound from '../ProductNotFound';
import StaticPagination from '../../atoms/StaticPagination';
import { IFilterOptions } from '../../../interface/accessories.interface';
import convertSlugToNormalTextAccessories from '../../../helpers/utils/convertSlugToNormalTextAccessories';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';
import eventClickPackageServiceList from '../../../helpers/emarsys/package-service/eventClickPackageServiceList';
import { useDispatch, useSelector } from 'react-redux';
import eventClickCouponServiceList from '../../../helpers/emarsys/coupon-service/eventClickCouponServiceList';
import {
  BUTTON_NAME_SERVICE_COUPON,
  SECTION_NAME_SERVICE_COUPON,
} from '../../../constants/emarsys/service-coupon';
import { IRootState } from '../../../store';
import {
  BUTTON_NAME_SERVICE_PACKAGE,
  SECTION_NAME_SERVICE_PACKAGE,
} from '../../../constants/emarsys/service-package';
import useImpressionCouponServiceList from '../../../hooks/emarsys/coupon-service/useImpressionCouponServiceList';
import useImpressionPackageServiceList from '../../../hooks/emarsys/package-service/useImpressionPackageServiceList';

export interface ServicesListSectionProps {
  tabList?: { name: string; path: string }[];
  servicePackages?: IServicesPackages[];
  servicePackagesForFilter?: IServicesPackages[];
  onClickSorts?: (value: boolean) => void;
  onClickFilter?: (value: boolean) => void;
  typeID?: string;
  filterCount?: number;
  userCarsData?: any[];
  handleCollectDataLayer?;
  freeTextSearch?: string;
  isForAccList?: boolean;
  pagination?: {
    currentPage: number;
    pageSize: number;
    sort: string;
    totalPages: number;
    totalResults: number;
  };
  paginationBaseUrl?: string;
  categoryFilter?: IFilterOptions;
  impressionData?: {
    title: string;
    carModels: string[];
    breadcrumbItems: IBreadcrumbs[];
    customerNo?: string;
    carCategory?: string;
    filterValue?: {
      search: string;
      special_offer: string;
      car_model: string;
      order_by: string;
    };
    sectionName: string;
  };
  feature?: string;
}

const ServicesListSection = ({
  tabList,
  servicePackages,
  onClickSorts,
  onClickFilter,
  userCarsData,
  typeID,
  filterCount,
  handleCollectDataLayer,
  freeTextSearch,
  servicePackagesForFilter,
  isForAccList = false,
  pagination = null,
  paginationBaseUrl = '',
  categoryFilter,
  impressionData,
  feature,
}: ServicesListSectionProps) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state: IRootState) => state.auth);

  const symbolRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/;

  const router = useRouter();
  const listingPageImpressionRef = useRef(null);

  const currentAccessoriesCategory = categoryFilter?.values.find((value) => {
    if (typeof router.query?.carModel === 'string') {
      return (
        value?.code?.toLowerCase() ===
        convertSlugToNormalTextAccessories(router.query.carModel).toLowerCase()
      );
    }
  });

  const [recs, setRecs] = useState<IServicesPackages[]>([]);

  const handleClickSearch = (name: string) => {
    // Emarsys
    if (typeID === 'package') {
      eventClickPackageServiceList(
        dispatch,
        impressionData?.carCategory,
        impressionData?.title,
        impressionData?.breadcrumbItems,
        impressionData?.customerNo,
        SECTION_NAME_SERVICE_PACKAGE.FILTER,
        BUTTON_NAME_SERVICE_PACKAGE.SEARCH,
        impressionData?.carModels,
        { ...impressionData.filterValue, search: name }
      );
    }

    if (feature === 'COUPON_SERVICE_LIST') {
      eventClickCouponServiceList(
        dispatch,
        impressionData.carCategory,
        impressionData?.title,
        impressionData.breadcrumbItems,
        userData?.customerNo,
        SECTION_NAME_SERVICE_COUPON.FILTER,
        BUTTON_NAME_SERVICE_COUPON.SEARCH,
        { ...impressionData.filterValue, search: name }
      );
    }

    const isSlugPath = router.pathname.includes('/[category]');
    const isValidSeach = !emojiRegex().test(name) && !symbolRegex.test(name);
    if (isSlugPath) {
      if (name) {
        router.push({
          pathname: router.pathname,
          query: {
            ...router.query,
            q: isValidSeach ? name : 'invalid search',
          },
        });
      } else {
        const updatedquery = router.query;
        delete updatedquery.q;
        router.push({
          pathname: router.pathname,
          query: {
            ...updatedquery,
          },
        });
      }
    } else {
      if (name) {
        router.push({
          query: {
            q: isValidSeach ? name : 'invalid search',
          },
        });
      } else {
        const updatedquery = router.query;
        delete updatedquery.q;
        router.push({
          query: {
            ...updatedquery,
          },
        });
      }
    }

    setRecs([]);
  };

  const filterRecs = (serviceName: string) => {
    const category = router.query?.category;
    if (category) {
      setRecs(
        servicePackagesForFilter?.filter(
          (item) =>
            item?.name?.toLowerCase()?.includes(serviceName.toLowerCase()) &&
            item?.bodyType?.includes(category?.toString()?.toUpperCase())
        )?.length > 0
          ? servicePackagesForFilter.filter(
              (item) =>
                item?.name?.toLowerCase()?.includes(serviceName.toLowerCase()) &&
                item?.bodyType?.includes(category?.toString()?.toUpperCase())
            )
          : [{ code: 'code-placeholder' }]
      );
    } else {
      setRecs(
        servicePackagesForFilter?.filter((item) =>
          item?.name?.toLowerCase()?.includes(serviceName.toLowerCase())
        )?.length > 0
          ? servicePackagesForFilter.filter((item) =>
              item?.name?.toLowerCase()?.includes(serviceName.toLowerCase())
            )
          : [{ code: 'code-placeholder' }]
      );
    }
  };

  let searchDebouncerTimer;
  const handleSearch = (e) => {
    const newValue = e;
    clearTimeout(searchDebouncerTimer);
    searchDebouncerTimer = setTimeout(async () => {
      if (newValue.length >= 2) {
        // search for recomendations here
        filterRecs(newValue);
      } else {
        setRecs([]);
      }
    }, 500);
  };

  const formattedServicePackages = useMemo(
    () =>
      servicePackages.map((item) => {
        if (typeID === 'accessories') {
          return {
            ...item,
            isRelevantPackage: userCarsData.some((car) =>
              (item?.accessoriesCarModels || []).includes(car.carModel)
            ),
          };
        } else {
          return {
            ...item,
            isRelevantPackage: userCarsData.some((car) =>
              item?.modelCode?.some(
                (modelCode) => modelCode.toLowerCase() === car.modelCode?.toLowerCase()
              )
            ),
          };
        }
      }),
    [servicePackages, userCarsData, typeID]
  );

  if (feature === 'COUPON_SERVICE_LIST') {
    useImpressionCouponServiceList(
      listingPageImpressionRef,
      impressionData?.title,
      impressionData?.breadcrumbItems,
      'listing page',
      impressionData?.carModels
    );
  }

  if (feature === 'PACKAGE_SERVICE_LIST') {
    useImpressionPackageServiceList(
      listingPageImpressionRef,
      impressionData?.title,
      impressionData?.breadcrumbItems,
      'listing page',
      impressionData?.carModels
    );
  }
  return (
    <section>
      <div className="lg:relative">
        <Tabs
          from={typeID}
          tabs={tabList}
          itemClassName="!px-4"
          additionalTab={
            <BodyLargeSemibold className={'w-full lg:block hidden text-right'}>
              {servicePackages.length}{' '}
              <span className="font-normal">{servicePackages.length === 1 ? 'Item' : 'Items'}</span>
            </BodyLargeSemibold>
          }
          tabClassname="top-[60px] lg:top-[150px] xl:top-[165px]"
        >
          <div className="lg:hidden flex border-b px-4 gap-3 justify-between items-center bg-white">
            <div className={'w-full flex gap-1 max-w-[70px] py-3'}>
              <BodyLargeSemibold>{servicePackages.length}</BodyLargeSemibold>
              {servicePackages.length === 1 ? 'Item' : 'Items'}
            </div>
            <div className="flex gap-2">
              <button
                className="flex gap-3 items-center bg-[#F8F8F8] p-1 text-xs rounded font-medium"
                onClick={() => onClickSorts(true)}
              >
                <SortIcon /> Urutkan
              </button>

              <button
                className="flex gap-2 items-center bg-[#F8F8F8] pl-1 pr-2.5 text-xs rounded font-medium relative"
                onClick={() => onClickFilter(true)}
              >
                <FilterIcon /> Filter
                {/* will implement later if API is Ready to consume */}
                {filterCount !== 0 && (
                  <span className="bg-supportiveRed px-[7px] text-white rounded-full text-[10px] absolute -right-2 -top-2">
                    {filterCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="lg:hidden border-b px-4">
            {isForAccList ? (
              <SearchServices
                handleClickSearch={handleClickSearch}
                placeholderText="Cari Aksesoris & Spareparts"
                iconColor="#393536"
                onValueChange={handleSearch}
                freeTextSearch={freeTextSearch}
              />
            ) : (
              <SearchServices
                recs={recs}
                handleClickSearch={handleClickSearch}
                filterRecs={filterRecs}
                placeholderText={
                  typeID === 'coupon'
                    ? 'Cari kupon servis'
                    : typeID.includes('accessories')
                      ? 'Cari Aksesoris & Spareparts'
                      : 'Cari paket servis'
                }
                iconColor="#393536"
                onValueChange={handleSearch}
                freeTextSearch={freeTextSearch}
              />
            )}
          </div>
          {!servicePackages || servicePackages?.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <ProductNotFound />
            </div>
          ) : (
            <div
              className={`grid grid-cols-2 ${isForAccList ? 'md:grid-cols-3' : ''} lg:mt-7 ${isForAccList ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} xl:grid-cols-3 lg:gap-12`}
              ref={listingPageImpressionRef}
            >
              {formattedServicePackages
                .sort((a, b) => {
                  if (a.isRelevantPackage && !b.isRelevantPackage) {
                    return -1;
                  } else {
                    return 0;
                  }
                })
                .map((item: any, index) => (
                  <>
                    <CardService
                      typeID={typeID}
                      index={index}
                      url={`${process.env.NEXT_PUBLIC_BASE_DETAIL_URL}${isForAccList ? (item.url.startsWith('/') ? item.url.slice(1) : item.url) : convertStringToSlug(item?.carModelService).toLowerCase()}`}
                      image={item?.images}
                      title={item?.name}
                      price={item?.price?.value}
                      oldPrice={item?.strikeThroughPrice?.value}
                      tag={item?.tag || []}
                      usp={item?.serviceModelTag || []}
                      tabList={tabList}
                      code={item?.code}
                      handleCollectDataLayer={handleCollectDataLayer}
                      isRelevantPackage={item?.isRelevantPackage}
                      bodyTypeName={item.bodyTypeName}
                      isForAccList={isForAccList}
                      category={item?.accessoriesCategory}
                      impressionData={impressionData}
                    />
                  </>
                ))}
            </div>
          )}
        </Tabs>
      </div>
      {isForAccList && (
        <>
          <div className="flex items-center justify-center mt-6 lg:mt-9">
            <div className="items-center gap-1 text-lg lg:flex">
              <StaticPagination
                totalCount={pagination?.totalResults}
                totalPages={pagination?.totalPages}
                pageSize={pagination?.pageSize}
                paginationBaseUrl={paginationBaseUrl}
              />
            </div>
          </div>
          {currentAccessoriesCategory?.isSparePartRequest && (
            <div className="my-6 bg-reliableBlack5">
              <p className="text-[14px] sm:text-[16px] p-4 ">
                Tidak menemukan item yang Anda cari? Buat request{' '}
                <a
                  href={`${process.env.NEXT_PUBLIC_URL}/services-toyota/suku-cadang`}
                  className="cursor-pointer text-hyperLink"
                >
                  {' '}
                  disini
                </a>
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
};

ServicesListSection.defaultProps = {
  tabList: [],
  servicePackages: [],
};

export default ServicesListSection;
